import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useForm } from 'react-hook-form';
import { Await, useLoaderData } from 'react-router';
import { FieldReportFormSchema, fieldReportSchema } from './fieldReportSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { MeasurementGroupLoaderResponse } from '../types';
import { useIsDesktopSize } from '@/hooks/useIsDesktopSize';
import { cn } from '@/lib/utils';
import { Suspense, useState } from 'react';
import { FieldReport, SWAP_REASONS, SWAP_REASONS_LIST } from './types';
import { useMutation } from '@tanstack/react-query';
import { getUIErrorMessage } from '@/utils/errors';
import { toast } from 'sonner';
import Spinner from '@/components/spinner/Spinner';
import ErrorAlert from '@/components/error-alert/ErrorAlert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import RHFDatePicker from '@/components/date-picker/RHFDatePicker';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { useGetProcessObjectsByIds } from '@/routes/process-objects/list/useGetProcessObjectsByIds';

export type FieldReportFormProps = {
	isOpen: boolean;
	title?: string;
	fieldReport?: FieldReport;
	onSubmit: (measurementGroupId: number, values: FieldReportFormSchema) => Promise<unknown>;
	submitSuccessMessage?: string;
	confirmationMessage?: string;
	onClose: () => void;
};

export const FieldReportForm = (props: FieldReportFormProps) => {
	const {
		isOpen,
		title,
		onClose,
		onSubmit,
		fieldReport,
		confirmationMessage = 'Are you sure you want to save these changes?',
		submitSuccessMessage = 'Saved successfuly'
	} = props;

	const {
		measurementGroup: { data: measurementGroupData },
		deploymentTypes
	} = useLoaderData() as MeasurementGroupLoaderResponse;

	const { paginatedProcessObjects } = useGetProcessObjectsByIds(
		measurementGroupData.devices.map(device => device.poId.toString()),
		measurementGroupData.devices.length > 0
	);

	const processObjectsList = paginatedProcessObjects?.data.items ?? [];

	const [showConfirmation, setShowConfirmation] = useState(false);
	const [error, setError] = useState('');

	const fieldReportForm = useForm<FieldReportFormSchema>({
		resolver: zodResolver(fieldReportSchema),
		defaultValues: {
			poId: fieldReport?.poId.toString() ?? '',
			performedAt: fieldReport?.performedAt ? new Date(fieldReport.performedAt) : undefined,
			deploymentTypeId: fieldReport?.deploymentTypeId.toString() ?? '',
			notes: fieldReport?.notes ?? '',
			hasExternalAntennas: fieldReport?.hasExternalAntennas ?? false,
			swapReason: fieldReport?.swapReason ?? null
		}
	});

	const { isDesktop } = useIsDesktopSize();

	const { isPending: isSavingReport, mutate: saveReport } = useMutation({
		mutationFn: (fieldReport: FieldReportFormSchema) => onSubmit(measurementGroupData.id, fieldReport),
		mutationKey: ['measurement-groups', measurementGroupData.toString(), 'field-reports'],
		onSuccess: () => {
			onClose();
			fieldReportForm.reset();
			setShowConfirmation(false);
			toast(submitSuccessMessage);
		},
		onError: error => {
			setError(getUIErrorMessage(error));
		}
	});

	const handleSaveReport = async () => {
		const submitForm = fieldReportForm.handleSubmit(values => {
			const payload = { ...values };
			if (values.swapReason === SWAP_REASONS.NOT_A_SWAP) {
				payload.swapReason = null;
			}
			saveReport(payload);
		});
		await submitForm();
	};

	const handleCloseForm = () => {
		onClose();
		setShowConfirmation(false);
		setError('');
	};

	return (
		<Sheet open={isOpen} onOpenChange={handleCloseForm}>
			<SheetContent
				side={!isDesktop ? 'bottom' : 'right'}
				className={cn('h-full p-10 overflow-auto', { 'sm:max-w-xl ': isDesktop, 'w-full h-5/6': !isDesktop })}
			>
				<SheetHeader>
					<SheetTitle>{title}</SheetTitle>
					<SheetDescription>Fill in the report details. Click save when you're done.</SheetDescription>
				</SheetHeader>
				<Form {...fieldReportForm}>
					<form className="flex flex-col gap-3">
						<FormField
							control={fieldReportForm.control}
							name="poId"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Process object</FormLabel>

									<Select onValueChange={field.onChange} value={field.value ?? ''}>
										<FormControl>
											<SelectTrigger className="w-full">
												<SelectValue placeholder="Select the process object" />
											</SelectTrigger>
										</FormControl>

										<SelectContent>
											{processObjectsList.map(po => (
												<SelectItem key={po.id} value={po.id.toString()}>
													<span className="font-medium">{po.id}</span> - {po.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<FormMessage />
								</FormItem>
							)}
						/>

						<RHFDatePicker
							control={fieldReportForm.control}
							name="performedAt"
							label="Date"
							placeholder="Pick a date"
							isFullWidth
						/>

						<FormField
							control={fieldReportForm.control}
							name="deploymentTypeId"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Deployment type</FormLabel>

									<Select onValueChange={field.onChange} value={field.value ?? ''}>
										<FormControl>
											<SelectTrigger className="w-full">
												<SelectValue placeholder="Select the deployment type" />
											</SelectTrigger>
										</FormControl>
										<Suspense
											fallback={
												<SelectContent>
													<Spinner />
												</SelectContent>
											}
										>
											<Await resolve={deploymentTypes}>
												{deploymentTypes => (
													<SelectContent>
														{deploymentTypes.data.map(t => (
															<SelectItem key={t.id} value={t.id.toString()}>
																{t.deploymentType}
															</SelectItem>
														))}
													</SelectContent>
												)}
											</Await>
										</Suspense>
									</Select>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={fieldReportForm.control}
							name="hasExternalAntennas"
							render={({ field }) => (
								<FormItem className="flex flex-row items-start space-x-3 space-y-0">
									<FormControl>
										<Checkbox
											checked={field.value}
											onCheckedChange={() => field.onChange(!field.value)}
										/>
									</FormControl>
									<div className="space-y-1 leading-none">
										<FormLabel>Has external antennas</FormLabel>
									</div>
								</FormItem>
							)}
						/>

						<FormField
							control={fieldReportForm.control}
							name="swapReason"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Swap reason</FormLabel>

									<Select onValueChange={field.onChange} value={field.value ?? ''}>
										<FormControl>
											<SelectTrigger className="w-full">
												<SelectValue placeholder="Select the swap reason" />
											</SelectTrigger>
										</FormControl>
										<SelectContent>
											{SWAP_REASONS_LIST.map(t => (
												<SelectItem key={t.id} value={t.id.toString()}>
													{t.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={fieldReportForm.control}
							name="notes"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Notes</FormLabel>
									<FormControl>
										<Textarea
											value={field.value}
											onChange={field.onChange}
											onBlur={field.onBlur}
											name={field.name}
											placeholder="Add any notes here"
											rows={4}
											className="text-sm"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</form>
				</Form>

				{error && <ErrorAlert title="Error" description={error}></ErrorAlert>}

				<SheetFooter>
					{!showConfirmation ? (
						<Button type="submit" onClick={() => setShowConfirmation(true)}>
							Save Changes
						</Button>
					) : (
						<div className="flex flex-col gap-2 text-sm justify-center items-center">
							<p>{confirmationMessage}</p>
							<div className="flex gap-2 w-full justify-center">
								<Button
									className="flex-1"
									variant="secondary"
									onClick={() => {
										setShowConfirmation(false);
										setError('');
									}}
									disabled={isSavingReport}
								>
									Cancel
								</Button>
								<Button
									className="flex-1"
									type="submit"
									onClick={handleSaveReport}
									disabled={isSavingReport}
								>
									{error ? 'Try again ' : 'Save'}
									{isSavingReport && <Spinner className="ml-2" />}
								</Button>
							</div>
						</div>
					)}
				</SheetFooter>
			</SheetContent>
		</Sheet>
	);
};
